import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_FaceShapeDetector = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/face-shape-detector',
			name: 'Face Shape Detector \u2014 Identify Your Face & Feature Shapes',
			description:
				'Upload a portrait to detect your face shape plus lip, nose, eye, and eyebrow shapes. Get a detailed AI breakdown in seconds. Try Lift free!',
			url: 'https://liftapp.ai/face-shape-detector',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/face-shape-detector#product' },
				{ '@id': 'https://liftapp.ai/face-shape-detector#howto' },
				{ '@id': 'https://liftapp.ai/face-shape-detector#benefits' },
				{ '@id': 'https://liftapp.ai/face-shape-detector#faq' },
				{ '@id': 'https://liftapp.ai/face-shape-detector#why-lift' },
				{ '@id': 'https://liftapp.ai/face-shape-detector#explore-tools' },
				{ '@id': 'https://liftapp.ai/face-shape-detector#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/face-shape-detector#product',
			name: 'Face Shape Detector',
			description:
				'Upload one portrait and let AI break down your face, lip, nose, eye, and eyebrow shapes. Compare results, save the report, or try a new photo \u2014 free to try!',
			url: 'https://liftapp.ai/face-shape-detector',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/face-shape-detector#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your portrait',
					text: 'Choose a clear front-facing photo from your device to start the face shape analysis',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'AI analyzes your features',
					text: 'The detector measures facial geometry and key proportions to identify each shape automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'View the shape breakdown',
					text: 'See your face, lip, nose, eye, and eyebrow shapes in one detailed result card',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Try another photo',
					text: 'Upload a new portrait or compare results across different angles to refine the breakdown',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/face-shape-detector#benefits',
			name: 'Who can benefit from the Face Shape Detector?',
			description:
				"Lift's Face Shape Detector is useful for beauty enthusiasts, hairstyle planners, eyewear shoppers, and anyone curious about their face and feature shapes.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Beauty & makeup enthusiasts \u2014 Identify your face and feature shapes before choosing contouring, lip, and brow techniques. Use the breakdown to pick products that actually suit you.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Hairstyle planners \u2014 Find out your face shape before booking a haircut and pick styles that flatter your features. Save the result and share it directly with your stylist.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Eyewear shoppers \u2014 Identify your face shape to narrow down glasses and sunglasses frames in seconds. Skip the endless trial-and-error of trying frames in stores or online.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Curious users \u2014 Get a clear face shape answer with a single photo upload. Compare results across multiple portraits to see how angle and lighting affect detection.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Mobile users \u2014 Upload from your phone, view the full shape breakdown, and share results in a tap. The interface is mobile-optimized so each step stays fast and easy to navigate.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Face Shape Detector by Lift',
				description:
					'Example of how Lift Face Shape Detector identifies your face and feature shapes from a single portrait.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/face-shape-detector#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Face Shape Detector?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Face Shape Detector is an AI tool that identifies the shape of your face and key features from a single portrait. The result breaks down face, lip, nose, eye, and eyebrow shapes in seconds.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Face Shape Detector work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a clear front-facing portrait, and the AI measures facial geometry and key feature proportions. Lift returns a detailed shape breakdown that you can review on screen or save right away.',
					},
				},
				{
					'@type': 'Question',
					name: 'Which feature shapes does the tool detect?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The detector identifies your overall face shape along with lip, nose, eye, and eyebrow shapes. Each feature is reported separately so you can review the full breakdown at a glance.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG \u2014 ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/face-shape-detector#why-lift',
			name: "Why Lift's Face Shape Detector stands out",
			description:
				"Lift's Face Shape Detector combines professional-grade AI, ease of use, and affordability \u2014 making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift Face Shape Detector',
				description:
					"Comparison of Lift's Face Shape Detector benefits \u2014 professional quality, AI accuracy, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/face-shape-detector#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more \u2013 all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/face-shape-detector#all-in-one',
			name: 'Try Lift \u2013 AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Face Shape Detector',
					item: 'https://liftapp.ai/face-shape-detector',
				},
			],
		},
	],
};
